import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppShell } from '../contexts/AppShellContext';
import { useProxyData, type ProxyApproval } from '../contexts/ProxyDataContext';
import { useTeamMessages } from '../contexts/TeamMessagesContext';
import type { ProxyFollowUpRecord, ProxyJob } from '../data/mockProxyJobs';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { ticketFamily, TICKET_LABEL, type TicketFamily } from '../data/proxyWorkflowRegistry';
import { OUTREACH_KIND } from '../utils/proxyFollowUpTracker';
import { SearchInput } from '../components/SearchInput';
import { PageHeader } from '../components/PageHeader';
import { inWindow, type ScopeValue } from '../utils/timeWindow';
import { proxyWindowBounds, proxyWindowCounts } from '../utils/proxyTimeWindow';

// Completed: every ticket that's been closed, newest first. A ticket is one
// case (a shortage from Fulfillment's hand-off to the merge, a contest from
// outreach to Discovery, an email routed…). Open one to see its history.
const ONE_STEP: TicketFamily[] = ['routing', 'rdq', 'contact', 'unmatched'];

// Which ticket a record from before tickets existed belongs to.
function familyOfRecord(type: string): TicketFamily | null {
  if (['shortage_outreach', 'Material Short', 'shortage_closed'].includes(type)) return 'shortage';
  if (['contest_outreach', 'signature_event', 'discovery_handoff', 'contest_form_received'].includes(type)) return 'contest';
  if (OUTREACH_KIND[type] === 'material' || ['material_followup_documented', 'filing_pulled', 'vif_bre_only'].includes(type)) return 'material';
  return null;
}

type Step =
  | { kind: 'approval'; at: string; approval: ProxyApproval }
  | { kind: 'record'; at: string; record: ProxyFollowUpRecord }
  | { kind: 'message'; at: string; from: string; title: string; body: string };

interface Ticket {
  key: string;
  family: TicketFamily;
  job: ProxyJob | undefined;
  jobId: string;
  closedAt: string;
  closedBy: string;
  steps: Step[];
}

function dayLabel(at: string): string {
  const day = at.slice(0, 10);
  if (day === PROXY_SEED_TODAY) return 'Today';
  return new Date(`${day}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function StepView({ step }: { step: Step }) {
  const [open, setOpen] = useState(false);
  if (step.kind === 'approval') {
    const a = step.approval;
    return (
      <div className="text-xs text-ink">
        <b className="font-semibold">Approved</b> by {a.by}: {a.title}{' '}
        <span className={a.edits.length ? 'text-copy' : 'text-[#0F6E56]'}>({a.edits.length ? `changed ${a.edits.join(', ')}` : 'as Ace drafted it'})</span>
      </div>
    );
  }
  if (step.kind === 'message') {
    return (
      <div className="text-xs text-ink">
        <b className="font-semibold">{step.from}:</b> {step.title}
        <div className="text-copy mt-0.5">{step.body}</div>
      </div>
    );
  }
  const r = step.record;
  if (r.recipientEmail === 'mic@betanxt.com') {
    return <div className="text-xs text-ink"><b className="font-semibold">MIC entry:</b> {r.bodyPreview}</div>;
  }
  return (
    <div className="text-xs text-ink">
      <button onClick={() => setOpen(o => !o)} className="text-left hover:underline">
        <b className="font-semibold">Email to {r.recipientEmail}:</b> {r.subject} <span className="text-teal-600">{open ? 'Hide' : 'Show email'}</span>
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg border border-trim p-3">
          {r.ccEmails?.length ? <div className="text-muted mb-1">Cc {r.ccEmails.join(', ')}</div> : null}
          <div className="text-sm leading-relaxed whitespace-pre-line">{r.bodyPreview}</div>
        </div>
      )}
    </div>
  );
}

export function ProxyCompletedPage() {
  const { setTopbar } = useAppShell();
  const { jobs, approvals } = useProxyData();
  const { messages } = useTeamMessages();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  // ?open= opens a ticket on arrival (the demo guide uses it).
  const [openKey, setOpenKey] = useState<string | null>(() => searchParams.get('open'));
  // Opens on the last 7 days, so recent work shows even before anything is closed today.
  const [scope, setScope] = useState<ScopeValue>({ window: 'week' });

  useEffect(() => {
    setTopbar('Proxy: Completed', 'Every ticket that\'s been closed, newest first. Open one to see its history from start to finish.');
  }, [setTopbar]);

  const tickets: Ticket[] = useMemo(() => {
    const jobById = new Map(jobs.map(j => [j.jobId, j]));
    // Approvals from before tickets existed: one-step work closed when approved.
    const withKeys = approvals.map(a => {
      const family = ticketFamily(a.workflowType, jobById.get(a.jobId));
      const key = a.ticketKey ?? (ONE_STEP.includes(family) ? `${family}:${a.taskId}` : `${family}:${a.jobId}`);
      return { a, family, key, closes: a.closesTicket ?? ONE_STEP.includes(family) };
    });
    const closing = new Map<string, (typeof withKeys)[number]>();
    withKeys.filter(x => x.closes).forEach(x => {
      const prev = closing.get(x.key);
      if (!prev || x.a.at > prev.a.at) closing.set(x.key, x);
    });
    return [...closing.values()].map(({ a, family, key }) => {
      const job = jobById.get(a.jobId);
      const steps: Step[] = withKeys.filter(x => x.key === key).map(x => ({ kind: 'approval' as const, at: x.a.at, approval: x.a }));
      (job?.followUpHistory || [])
        .filter(r => (r.ticketKey ? r.ticketKey === key : !ONE_STEP.includes(family) && familyOfRecord(r.type) === family))
        .forEach(r => steps.push({ kind: 'record', at: r.timestamp, record: r }));
      if (family === 'shortage') {
        messages.filter(m => m.jobId === a.jobId).forEach(m => steps.push({
          kind: 'message', at: m.at, from: m.from === 'wayne' ? 'Fulfillment to Proxy' : 'Proxy to Fulfillment', title: m.title, body: m.body,
        }));
      }
      steps.sort((x, y) => x.at.localeCompare(y.at));
      return { key, family, job, jobId: a.jobId, closedAt: a.at, closedBy: a.by, steps };
    }).sort((x, y) => y.closedAt.localeCompare(x.closedAt));
  }, [jobs, approvals, messages]);

  const counts = useMemo(() => proxyWindowCounts(tickets.map(t => ({ intake_date: t.closedAt.slice(0, 10) }))), [tickets]);
  const filtered = useMemo(() => {
    const bounds = proxyWindowBounds(scope);
    const q = search.trim().toLowerCase();
    return tickets
      .filter(t => inWindow(t.closedAt.slice(0, 10), bounds))
      .filter(t => !q || t.jobId.includes(q) || (t.job?.issuer ?? '').toLowerCase().includes(q) || TICKET_LABEL[t.family].toLowerCase().includes(q));
  }, [tickets, search, scope]);

  const days = useMemo(() => {
    const byDay = new Map<string, Ticket[]>();
    filtered.forEach(t => byDay.set(dayLabel(t.closedAt), [...(byDay.get(dayLabel(t.closedAt)) || []), t]));
    return [...byDay];
  }, [filtered]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
    <div data-tour="completed-period"><PageHeader scope={scope} onScopeChange={setScope} counts={counts} /></div>
    <div className="flex-1 overflow-auto px-6 py-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold whitespace-nowrap bg-wash border border-trim rounded-full px-3 py-1 text-copy">
          {filtered.length} closed ticket{filtered.length === 1 ? '' : 's'}
        </span>
        <SearchInput value={search} onChange={setSearch} placeholder="Search issuer, job #, kind of work…" width={280} />
      </div>

      {filtered.length === 0 ? (
        <div className="px-4 py-10 rounded-xl border border-trim bg-wash text-center">
          <div className="text-sm font-medium text-ink mb-0.5">Nothing closed yet</div>
          <div className="text-xs text-muted">A ticket lands here once its last step is approved.</div>
        </div>
      ) : days.map(([day, rows]) => (
        <div key={day} className="mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted mb-2">{day}</div>
          <ul data-tour="completed-list" className="rounded-xl border border-trim bg-white divide-y divide-trim">
            {rows.map(t => {
              const open = openKey === t.key;
              return (
                <li key={t.key}>
                  <button onClick={() => setOpenKey(open ? null : t.key)} className="w-full text-left px-4 py-3 flex items-center gap-4 hover:bg-tint">
                    <span className="text-xs text-muted whitespace-nowrap w-12">{t.closedAt.slice(11)}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-ink truncate">{t.job?.issuer ?? `Scan ${t.jobId}`}: {TICKET_LABEL[t.family]}</span>
                      <span className="block text-xs text-muted">Job {t.jobId} · {t.steps.length} step{t.steps.length === 1 ? '' : 's'}</span>
                    </span>
                    <span className="text-xs text-muted whitespace-nowrap">Closed by {t.closedBy}</span>
                    <span className={`text-muted transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
                  </button>
                  {open && (
                    <ol data-tour="completed-timeline" className="px-4 pb-4 pl-20 flex flex-col gap-2.5 border-l-2 border-trim ml-12">
                      {t.steps.map((step, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="text-[11px] text-muted whitespace-nowrap w-24 pt-px">{step.at.slice(5, 10).replace('-', '/')} {step.at.slice(11)}</span>
                          <div className="flex-1 min-w-0"><StepView step={step} /></div>
                        </li>
                      ))}
                    </ol>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
    </div>
  );
}
